import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export interface SendMessageRequest {
    conversationId: string;
    content: string;
    recipientPhoneNumber: string;
    senderId: string;
    mediaType?: 'None' | 'Image' | 'Document' | 'Audio' | 'Video';
    mediaUrl?: string;
}

export interface MessageDto {
    id: string;
    conversationId: string;
    content: string;
    channel: string;
    direction: string;
    status: string;
    whatsAppMessageId?: string;
    senderId: string;
    recipientPhoneNumber?: string;
    mediaType: string;
    mediaUrl?: string;
    createdAt: string;
    updatedAt?: string;
}

export interface ConversationDto {
    id: string;
    contactId: string;
    contactName: string;
    contactAvatar?: string;
    channel: string;
    lastMessageAt?: string;
    unreadCount: number;
    lastMessagePreview?: string;
    createdAt: string;
}

export interface PagedResult<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

export interface CommandResult<T = any> {
    success: boolean;
    errorCode?: string;
    message?: string;
    data?: T;
}

class WhatsAppService {
    private getAuthToken(): string | null {
        return localStorage.getItem('authToken');
    }

    private getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
        };
    }

    async getConversations(channel?: string, onlyUnread: boolean = false): Promise<CommandResult<ConversationDto[]>> {
        try {
            const params = new URLSearchParams();
            if (channel) params.append('channel', channel);
            if (onlyUnread) params.append('onlyUnread', 'true');

            const response = await axios.get(
                `${API_BASE_URL}/chat/conversations?${params.toString()}`,
                { headers: this.getAuthHeaders() }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching conversations:', error);
            return {
                success: false,
                errorCode: 'FETCH_CONVERSATIONS_ERROR',
                message: error.response?.data?.message || error.message
            };
        }
    }

    async getMessages(
        conversationId: string,
        pageNumber: number = 1,
        pageSize: number = 50
    ): Promise<CommandResult<PagedResult<MessageDto>>> {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/chat/conversations/${conversationId}/messages?pageNumber=${pageNumber}&pageSize=${pageSize}`,
                { headers: this.getAuthHeaders() }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error fetching messages:', error);
            return {
                success: false,
                errorCode: 'FETCH_MESSAGES_ERROR',
                message: error.response?.data?.message || error.message
            };
        }
    }

    async sendMessage(request: SendMessageRequest): Promise<CommandResult<string>> {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/chat/messages`,
                request,
                { headers: this.getAuthHeaders() }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error sending message:', error);
            return {
                success: false,
                errorCode: 'SEND_MESSAGE_ERROR',
                message: error.response?.data?.message || error.message
            };
        }
    }
}

export const whatsappService = new WhatsAppService();
export default whatsappService;
